<template>
  <div
    id="customer-reviews-content"
    class="w-100 d-lg-inline-block pb-0 pb-lg-3 px-2"
  >
    <div class="v-row pa-3 p-lg-0 flex-wrap-reverse my-5 justify-center">
      <ReviewStatistics
        class="v-col-12 v-col-sm-5 v-col-lg-6 mt-0 mt-md-4"
        :ratings="ratingStatistics"
      />
      <HeartRatingSummary
        class="v-col-12 v-col-sm-5 v-col-lg-6"
        :rating="product.customer_rating"
        :ratings-count="product.customer_rating_count"
      />
    </div>
    <div class="position-relative pt-2" v-if="toolTypes">
      <div class="reviews-list v-col-12">
        <ReviewFilters @update="changedFilters" />
        <div
          v-if="!reviews.length"
          class="v-row pt-5 d-flex justify-space-around c-gray"
        >
          {{ translate('pdp_customer_reviews_empty') }}
        </div>
        <CustomerReview
          v-for="review in reviews"
          :key="review.review_id_hash"
          :review="review"
          :tool-types="toolTypes"
        />
      </div>
      <div v-if="totalPages > 1" class="mb-3 mb-lg-0">
        <VPagination
          v-model="currentPage"
          :total-visible="$vuetify.display.smAndUp ? 5 : 1"
          :length="totalPages"
          @update:modelValue="onPageChange"
        />
      </div>
      <LoadingOverlay v-if="loading" />
    </div>
  </div>
</template>

<script>
import CustomerReview from '@/components/pdp/atoms/CustomerReview.vue'
import { defineAsyncComponent } from 'vue'

import { useCustomerReviewsStore } from '@/stores/customerReviews'
import { usePdpStore } from '@/stores/pdp'
import { mapActions, mapState } from 'pinia'

const HeartRatingSummary = defineAsyncComponent(
  () => import('@/components/pdp/molecules/HeartRatingSummary.vue')
)
const ReviewFilters = defineAsyncComponent(
  () => import('@/components/pdp/molecules/ReviewFilters.vue')
)
const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const ReviewStatistics = defineAsyncComponent(
  () => import('@/components/basic/molecules/ReviewStatistics.vue')
)

const SELECTOR_REVIEWS_LIST = '#customer-reviews'
const EMPTY_RATING_STATISTICS = { total_count: 0, values: [] }

export default {
  name: 'CustomerReviews',
  components: {
    ReviewFilters,
    LoadingOverlay,
    HeartRatingSummary,
    ReviewStatistics,
    CustomerReview
  },
  data() {
    return {
      currentPage: 1,
      sort: 'helpful_desc',
      onlyWithText: true,
      loading: false,
      toolTypes: null
    }
  },
  computed: {
    ...mapState(useCustomerReviewsStore, ['reviews', 'totalPages']),
    ...mapState(usePdpStore, { product: 'getApiProduct' }),
    ratingStatistics() {
      // The embed already comes in the shape ReviewStatistics renders (VIP-12633)
      return this.product.customer_rating_statistics ?? EMPTY_RATING_STATISTICS
    }
  },
  methods: {
    ...mapActions(useCustomerReviewsStore, { load: 'loadReviews' }),
    async onPageChange(page) {
      const [{ default: DomHelper }] = await Promise.all([
        await import('@/services/helpers/DomHelper'),
        await this.loadReviews()
      ])
      this.currentPage = page
      DomHelper.scrollTo(SELECTOR_REVIEWS_LIST)
    },
    async loadReviews() {
      this.loading = true
      await this.load({
        productId: this.product.product_id,
        onlyWithText: this.onlyWithText,
        sort: this.sort,
        page: this.currentPage
      })
      this.loading = false
    },
    changedFilters({ withText, sort }) {
      this.currentPage = 1
      this.onlyWithText = withText
      this.sort = sort
      this.loadReviews()
    }
  },
  // mounted, not created: created also runs during a server render, where it
  // would start a floating fetch whose result never makes it into the
  // response (VIP-12038)
  async mounted() {
    const [, { TYPES }] = await Promise.all([
      await this.loadReviews(),
      await import('@/constants/AdminTool')
    ])
    this.toolTypes = TYPES
  }
}
</script>
